import React, { useEffect, useMemo, useRef, useState } from 'react';
import Input from '../Input';
import LinkButton from '../../../Button/LinkButton/LinkButton';
import styles from './Code.module.scss';

export type CodeInputType = 'numeric' | 'letters' | 'alfanumeric';

interface Props {
	length: number;
	type?: CodeInputType;
	onChange?: (value: string) => void;
	autoFocus?: boolean;
	disabled?: boolean;
}

const Code: React.FC<Props> = ({
	length,
	type = 'numeric',
	onChange,
	autoFocus = false,
	disabled = false,
}) => {
	const allowedRegex = useMemo(() => {
		switch (type) {
			case 'numeric':
				return /^[0-9]$/;
			case 'letters':
				return /^[A-Za-z]$/;
			case 'alfanumeric':
			default:
				return /^[A-Za-z0-9]$/;
		}
	}, [type]);

	const [values, setValues] = useState<string[]>(
		Array.from({ length }, () => ''),
	);

	const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

	useEffect(() => {
		setValues(prev => {
			const next = Array.from({ length }, (_, i) => prev[i] ?? '');
			return next;
		});
	}, [length]);

	useEffect(() => {
		onChange?.(values.join(''));
	}, [values, onChange]);

	useEffect(() => {
		if (autoFocus) {
			inputsRef.current[0]?.focus();
		}
	}, [autoFocus]);

	const sanitizeOneChar = (raw: string) => {
		if (!raw) return '';
		const ch = raw.slice(-1);
		return allowedRegex.test(ch) ? ch : '';
	};

	const handleChange = (index: number, raw: string) => {
		const val = sanitizeOneChar(raw);

		setValues(prev => {
			const next = [...prev];
			next[index] = val;
			return next;
		});

		if (val && index < length - 1) {
			inputsRef.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === 'Backspace') {
			if (!values[index] && index > 0) {
				inputsRef.current[index - 1]?.focus();
			}
		}

		if (e.key === 'ArrowLeft' && index > 0) {
			inputsRef.current[index - 1]?.focus();
		}

		if (e.key === 'ArrowRight' && index < length - 1) {
			inputsRef.current[index + 1]?.focus();
		}
	};

	const handlePaste = (
		index: number,
		e: React.ClipboardEvent<HTMLInputElement>,
	) => {
		const text = e.clipboardData.getData('text') ?? '';
		if (!text) return;

		e.preventDefault();

		const chars = text.split('').filter(ch => allowedRegex.test(ch));

		if (chars.length === 0) return;

		setValues(prev => {
			const next = [...prev];
			let i = index;

			for (const ch of chars) {
				if (i >= length) break;
				next[i] = ch;
				i++;
			}

			const focusIndex =
				Math.min(
					next.findIndex((v, idx) => idx >= index && v === ''),
					length - 1,
				) === -1
					? Math.min(index + chars.length - 1, length - 1)
					: Math.min(
							next.findIndex((v, idx) => idx >= index && v === ''),
							length - 1,
					  );

			queueMicrotask(() => inputsRef.current[focusIndex]?.focus());

			return next;
		});
	};

	const handlePasteFromClipboard = async () => {
		try {
			const text = (await navigator.clipboard.readText()) ?? '';
			if (!text) return;

			const chars = text
				.split('')
				.filter(ch => allowedRegex.test(ch))
				.slice(0, length);

			if (chars.length === 0) return;

			const next = Array.from({ length }, (_, i) => chars[i] ?? '');

			setValues(next);

			const firstEmpty = next.findIndex(v => v === '');
			const focusIndex =
				firstEmpty === -1
					? Math.min(chars.length - 1, length - 1)
					: Math.min(firstEmpty, length - 1);

			queueMicrotask(() => inputsRef.current[focusIndex]?.focus());
		} catch (e) {
			console.error('Failed to paste text from clipboard');
		}
	};

	const inputMode = type === 'numeric' ? 'numeric' : 'text';

	return (
		<div className={styles.wrapper}>
			<div className={styles.codeWrapper}>
				{values.map((value, index) => (
					<Input
						key={index}
						ref={el => (inputsRef.current[index] = el)}
						type='text'
						inputMode={inputMode}
						maxLength={1}
						value={value}
						disabled={disabled}
						onChange={e => handleChange(index, e.target.value)}
						onKeyDown={e => handleKeyDown(index, e)}
						onPaste={e => handlePaste(index, e)}
						autoComplete='one-time-code'
						style={{
							textAlign: 'center',
							padding: 0,
							flex: 1,
						}}
					/>
				))}
			</div>
			<div className={styles.buttonWrapper}>
				<LinkButton
					text='base.pasteCopied'
					theme='purple'
					onClick={handlePasteFromClipboard}
				/>
			</div>
		</div>
	);
};

export default Code;
